import { useMemo } from "react";

import { API_URLS } from "shared/constants";
import useHttpActions from "shared/services/api/core/useHttpActions";

import type { LogoutResult, RefreshTokenResult, ValidateResetPasswordCodeResult } from "shared/types/AuthModel";
import {
	confirmEmailByCodeResolver,
	loginCheckResult,
	loginResult,
	logoutResult,
	refreshTokenResult,
	validateResetPasswordCodeResult
} from "shared/types/AuthModel";

import BaseConverter from "./converters/baseConverter";

import appConfig from "../../config/appConfig";
import type { ConfirmEmailByCodeResult, LoginCheckResult, LoginRequest, LoginResult } from "../types/AuthModel";
import type { BaseApiResponse } from "../types/BaseApiResponse";
import { baseApiResponse } from "../types/BaseApiResponse";
import type { CreateProfileRequest } from "../types/UserModel";

const useAuthService = () => {
	const actions = useHttpActions();

	return useMemo(
		() => ({
			checkAuthStatus: async () => {
				return BaseConverter.convert<LoginCheckResult>(
					await actions.get(API_URLS.AUTH.CHECK, null, {
						resolvers: [
							{
								resolver: loginCheckResult
							}
						]
					})
				);
			},
			login: async (params: { data: LoginRequest; smsLogin?: number; recaptchaToken?: string }) => {
				return BaseConverter.convert<LoginResult>(
					await actions.put(API_URLS.AUTH.LOGIN(params.smsLogin), params.data, {
						headers: params.recaptchaToken
							? {
									"Access-Control-Expose-Headers": "X-Recaptcha",
									"X-Recaptcha": appConfig.GLOBAL_CONSTANTS.GOOGLE_RECAPTCHA_SITE_KEY,
									HTTP_X_RECAPTCHA: params.recaptchaToken
								}
							: undefined,
						resolvers: [
							{
								resolver: loginResult
							}
						]
					})
				);
			},
			refreshToken: async (refreshToken: string) => {
				return BaseConverter.convert<RefreshTokenResult>(
					await actions.put(
						API_URLS.AUTH.REFRESH_TOKEN,
						{
							token: refreshToken
						},
						{
							resolvers: [
								{
									resolver: refreshTokenResult
								}
							]
						}
					)
				);
			},
			validateRegisteringProfile: async (params: { email: string; password: string }) => {
				return BaseConverter.convert<BaseApiResponse>(
					await actions.post(
						API_URLS.AUTH.VALIDATE_REGISTERING_PROFILE,
						{
							data: {
								email: params.email,
								password: params.password
							},
							fields: ["email", "password"]
						},
						{
							resolvers: [
								{
									resolver: baseApiResponse
								}
							]
						}
					)
				);
			},
			signUp: async (data: Partial<CreateProfileRequest>, recaptchaToken?: string) => {
				return BaseConverter.convert<BaseApiResponse>(
					await actions.post(API_URLS.AUTH.SIGN_UP, data, {
						headers: recaptchaToken
							? {
									"Access-Control-Expose-Headers": "X-Recaptcha",
									"X-Recaptcha": appConfig.GLOBAL_CONSTANTS.GOOGLE_RECAPTCHA_SITE_KEY,
									HTTP_X_RECAPTCHA: recaptchaToken
								}
							: undefined,
						resolvers: [
							{
								resolver: baseApiResponse
							}
						]
					})
				);
			},
			confirmEmailByCode: async (code: string) => {
				return BaseConverter.convert<ConfirmEmailByCodeResult>(
					await actions.patch(
						API_URLS.AUTH.COMPLETE,
						{
							code,
							useJwt: 1
						},
						null,
						{
							resolvers: [
								{
									resolver: confirmEmailByCodeResolver
								}
							]
						}
					)
				);
			},
			logout: async () => {
				return BaseConverter.convert<LogoutResult>(
					await actions.delete(API_URLS.AUTH.LOGOUT, null, null, {
						resolvers: [
							{
								resolver: logoutResult
							}
						]
					})
				);
			},
			requestPasswordReset: async (email: string) => {
				return BaseConverter.convert<BaseApiResponse>(
					await actions.post(
						API_URLS.AUTH.REQUEST_PASSWORD_RESET,
						{
							email
						},
						{
							resolvers: [
								{
									resolver: baseApiResponse
								}
							]
						}
					)
				);
			},
			checkResetPasswordCode: async (code: string) => {
				return BaseConverter.convert<ValidateResetPasswordCodeResult>(
					await actions.get(
						API_URLS.AUTH.REQUEST_PASSWORD_RESET,
						{
							action: "checkRestoreCode",
							code
						},
						{
							resolvers: [
								{
									resolver: validateResetPasswordCodeResult
								}
							]
						}
					)
				);
			},
			resetPassword: async (password: string, code: string) => {
				return BaseConverter.convert<BaseApiResponse>(
					await actions.put(
						API_URLS.AUTH.REQUEST_PASSWORD_RESET,
						{
							newPassword: password,
							repeatPassword: password,
							code
						},
						{
							resolvers: [
								{
									resolver: baseApiResponse
								}
							]
						}
					)
				);
			},
			testProfile: async () => {
				await actions.get(API_URLS.AUTH.SIGN_UP);
			}
		}),
		[actions]
	);
};

export default useAuthService;
